import { createSlice } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";

const STORAGE_KEY = "sourceiq_supplier_registration_v2";

const TYPE_LABELS = {
  standard: "Standard",
  "pre-registration": "Pre-Registration Form",
  "post-registration": "Post-Registration Form",
};

export const FORM_TYPE_OPTIONS = [
  {
    id: "standard",
    title: "Standard Supplier Form",
    description: "General purpose supplier registration",
  },
  {
    id: "pre-registration",
    title: "Pre-Registration Form",
    description: "Collect initial information before full onboarding",
  },
  {
    id: "post-registration",
    title: "Post-Registration Form",
    description: "Additional details after initial registration",
  },
];

export const PALETTE_GROUPS = [
  {
    id: "content",
    label: "Content",
    items: [
      { id: "h1", label: "Section Title", icon: "H1" },
      { id: "h2", label: "Sub-title", icon: "H2" },
      { id: "body", label: "Body", icon: "B" },
    ],
  },
  {
    id: "input",
    label: "Input",
    items: [
      { id: "text", label: "Text Field", icon: "T" },
      { id: "dropdown", label: "Dropdown", icon: "dropdown" },
      { id: "checkbox", label: "Checkbox", icon: "checkbox" },
    ],
  },
  {
    id: "media",
    label: "Media",
    items: [
      { id: "logo", label: "Upload Logo", icon: "logo" },
      { id: "file", label: "Upload File", icon: "file" },
    ],
  },
];

const SEED_POST_SUBMISSIONS = [
  {
    id: "sub-1",
    company: "ACME Manufacturing",
    contactName: "Ashton Banks",
    email: "abanks@acme.com",
    formName: "Consumer hardware",
    submittedAt: "2025-02-20T10:00:00.000Z",
    status: "submitted",
  },
  {
    id: "sub-2",
    company: "Green Foods Ltd.",
    contactName: "Sarah Lee",
    email: "slee@gfoods.com",
    formName: "Compliance Extension Form",
    submittedAt: "2025-02-25T10:00:00.000Z",
    status: "under-review",
  },
  {
    id: "sub-3",
    company: "Horizon Plastics",
    contactName: "Daniel Wu",
    email: "dwu@hplastics.com",
    formName: "ESG Post-Registration Form",
    submittedAt: "2025-02-25T10:00:00.000Z",
    status: "approved",
  },
  {
    id: "sub-4",
    company: "Bright Metals Inc.",
    contactName: "Emily Chen",
    email: "echen@bmetals.com",
    formName: "ESG Post-Registration Form",
    submittedAt: "2025-02-28T10:00:00.000Z",
    status: "rejected",
    rejectReason: "",
  },
];

const SEED_PENDING = [
  {
    id: "pend-1",
    company: "Qualcomm",
    contactName: "Larry Hillsworth",
    email: "larry@qualcomm.com",
    formName: "Pre-Registration Form",
    submittedAt: "2025-03-16T22:00:00.000Z",
    status: "in-progress",
  },
  {
    id: "pend-2",
    company: "Apple",
    contactName: "Jane Doe",
    email: "jane@apple.com",
    formName: "Pre-Registration Form",
    submittedAt: "2025-03-16T11:15:00.000Z",
    status: "in-progress",
  },
];

const loadState = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) {
      return {
        forms: [],
        postSubmissions: SEED_POST_SUBMISSIONS,
        pendingSubmissions: SEED_PENDING,
      };
    }
    const parsed = JSON.parse(data);
    return {
      forms: parsed.forms || [],
      postSubmissions: parsed.postSubmissions?.length
        ? parsed.postSubmissions
        : SEED_POST_SUBMISSIONS,
      pendingSubmissions: parsed.pendingSubmissions?.length
        ? parsed.pendingSubmissions
        : SEED_PENDING,
    };
  } catch {
    return {
      forms: [],
      postSubmissions: SEED_POST_SUBMISSIONS,
      pendingSubmissions: SEED_PENDING,
    };
  }
};

const saveState = (state) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        forms: state.forms,
        postSubmissions: state.postSubmissions,
        pendingSubmissions: state.pendingSubmissions,
      })
    );
  } catch {
    /* ignore */
  }
};

export const createDefaultSections = () => [
  { id: uuidv4(), title: "General Information", isDefault: true, fields: [] },
  { id: uuidv4(), title: "Certifications", isDefault: true, fields: [] },
  { id: uuidv4(), title: "Documents", isDefault: true, fields: [] },
];

export const createFieldDefaults = (type) => {
  const base = { id: uuidv4(), type, required: false };
  switch (type) {
    case "h2":
      return { ...base, label: "Sub-title", content: "Sub-title" };
    case "body":
      return { ...base, label: "Body", content: "Body text" };
    case "text":
      return { ...base, label: "New Text field", placeholder: "Placeholder Text" };
    case "dropdown":
      return { ...base, label: "New dropdown field", options: ["Option 1"] };
    case "checkbox":
      return { ...base, label: "New checkbox field", options: ["Option 1"] };
    case "logo":
      return { ...base, label: "Upload Logo" };
    case "file":
      return { ...base, label: "File Upload" };
    default:
      return { ...base, label: "New field" };
  }
};

const persist = (state) => saveState(state);

const initialState = loadState();

const formsSlice = createSlice({
  name: "forms",
  initialState,
  reducers: {
    createForm: {
      prepare: ({ name, type, isRequest = false }) => ({
        payload: {
          id: uuidv4(),
          name: name?.trim() || TYPE_LABELS[type] || "Untitled Form",
          type,
          typeLabel: TYPE_LABELS[type] || "Standard",
          status: "draft",
          publishStatus: "active",
          isRequest: !!isRequest,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: "You",
          publicUrl: "",
          apiKey: "",
          sections: createDefaultSections(),
        },
      }),
      reducer: (state, action) => {
        state.forms.unshift(action.payload);
        persist(state);
      },
    },
    updateForm: (state, action) => {
      const form = state.forms.find((f) => f.id === action.payload.id);
      if (!form) return;
      Object.assign(form, action.payload.changes, {
        updatedAt: new Date().toISOString(),
      });
      persist(state);
    },
    saveDraft: (state, action) => {
      const form = state.forms.find((f) => f.id === action.payload);
      if (!form) return;
      form.status = "draft";
      form.updatedAt = new Date().toISOString();
      persist(state);
    },
    publishForm: (state, action) => {
      const form = state.forms.find((f) => f.id === action.payload);
      if (!form) return;
      form.status = "published";
      form.publishStatus = "active";
      form.updatedAt = new Date().toISOString();
      form.publicUrl =
        form.publicUrl ||
        `supplierregistration.com/${form.id.slice(0, 8).toLowerCase()}`;
      form.apiKey =
        form.apiKey ||
        Array.from({ length: 32 }, () =>
          Math.floor(Math.random() * 16).toString(16)
        ).join("");
      persist(state);
    },
    archiveForm: (state, action) => {
      const form = state.forms.find((f) => f.id === action.payload);
      if (!form) return;
      form.publishStatus =
        form.publishStatus === "archived" ? "active" : "archived";
      form.updatedAt = new Date().toISOString();
      persist(state);
    },
    deleteForm: (state, action) => {
      state.forms = state.forms.filter((f) => f.id !== action.payload);
      persist(state);
    },
    renameSection: (state, action) => {
      const { formId, sectionId, title } = action.payload;
      const form = state.forms.find((f) => f.id === formId);
      const section = form?.sections.find((s) => s.id === sectionId);
      if (!section) return;
      section.title = title.trim() || section.title;
      form.updatedAt = new Date().toISOString();
      persist(state);
    },
    addCustomSection: {
      prepare: (payload) => {
        const { formId, title } =
          typeof payload === "string"
            ? { formId: payload, title: "Heading 1" }
            : payload;
        return {
          payload: {
            formId,
            section: {
              id: uuidv4(),
              title: title || "Heading 1",
              isDefault: false,
              fields: [],
            },
          },
        };
      },
      reducer: (state, action) => {
        const { formId, section } = action.payload;
        const form = state.forms.find((f) => f.id === formId);
        if (!form) return;
        const count = form.sections.filter((s) => !s.isDefault).length;
        form.sections.push({
          ...section,
          title:
            section.title === "Heading 1" && count > 0
              ? `Heading 1 ${String.fromCharCode(65 + count)}`
              : section.title,
        });
        form.updatedAt = new Date().toISOString();
        persist(state);
      },
    },
    addField: (state, action) => {
      const { formId, sectionId, type } = action.payload;
      const form = state.forms.find((f) => f.id === formId);
      const section = form?.sections.find((s) => s.id === sectionId);
      if (!section) return;
      section.fields.push(createFieldDefaults(type));
      form.updatedAt = new Date().toISOString();
      persist(state);
    },
    updateField: (state, action) => {
      const { formId, sectionId, fieldId, changes } = action.payload;
      const form = state.forms.find((f) => f.id === formId);
      const section = form?.sections.find((s) => s.id === sectionId);
      const field = section?.fields.find((f) => f.id === fieldId);
      if (!field) return;
      Object.assign(field, changes);
      form.updatedAt = new Date().toISOString();
      persist(state);
    },
    deleteField: (state, action) => {
      const { formId, sectionId, fieldId } = action.payload;
      const form = state.forms.find((f) => f.id === formId);
      const section = form?.sections.find((s) => s.id === sectionId);
      if (!section) return;
      section.fields = section.fields.filter((f) => f.id !== fieldId);
      form.updatedAt = new Date().toISOString();
      persist(state);
    },
    updateSubmissionStatus: (state, action) => {
      const { id, status, rejectReason, collection } = action.payload;
      const list =
        collection === "pending"
          ? state.pendingSubmissions
          : state.postSubmissions;
      const item = list.find((s) => s.id === id);
      if (!item) return;
      item.status = status;
      if (rejectReason !== undefined) item.rejectReason = rejectReason;
      persist(state);
    },
    addForm: (state, action) => {
      state.forms.push(action.payload);
      persist(state);
    },
    clearAllForms: (state) => {
      state.forms = [];
      persist(state);
    },
  },
});

export const {
  createForm,
  updateForm,
  saveDraft,
  publishForm,
  archiveForm,
  deleteForm,
  renameSection,
  addCustomSection,
  addField,
  updateField,
  deleteField,
  updateSubmissionStatus,
  addForm,
  clearAllForms,
} = formsSlice.actions;

export const selectAllForms = (state) => state.forms.forms || [];
export const selectDraftForms = (state) =>
  (state.forms.forms || []).filter((f) => f.status === "draft" && !f.isRequest);
export const selectPublishedForms = (state) =>
  (state.forms.forms || []).filter((f) => f.status === "published");
export const selectPostSubmissions = (state) =>
  state.forms.postSubmissions || [];
export const selectPendingSubmissions = (state) =>
  state.forms.pendingSubmissions || [];

export default formsSlice.reducer;
